/**
 * company-lookup — gõ mã số thuế thì tên công ty và địa chỉ tự điền.
 *
 * Nguồn: GET https://api.vietqr.io/v2/business/{mã} — miễn phí, không cần khoá,
 * trả `access-control-allow-origin: *` nên trình duyệt gọi thẳng được. Dữ liệu
 * tổng hợp từ trang của Cục Thuế (gdt.gov.vn).
 *
 * ⚠️ DỮ LIỆU CHẬM, và chậm bao nhiêu thì mỗi hồ sơ một khác: đo 26/09/2026,
 * cùng một lượt tra, mã 0318027638 tự ghi "5 tháng trước" còn mã chi nhánh
 * 0318027638-001 ghi "9 ngày trước". Công ty vừa đổi địa chỉ hoàn toàn có thể
 * ra địa chỉ cũ. Đó là lý do luật ① dưới đây không bao giờ được gỡ.
 *
 * Ba luật giữ cho nó không phá dữ liệu người nhập:
 *   ① Chỉ điền vào ô đang TRỐNG. Ô đã có chữ là chữ của người nhập; máy chỉ mời
 *     "Dùng thông tin này" rồi chờ họ bấm.
 *   ② Mỗi mã chỉ tự điền một lần. Người nhập xoá đi gõ lại là ý họ.
 *   ③ Tra không ra thì KHÔNG khoá ô, KHÔNG chặn lưu, KHÔNG hiện lỗi đỏ. Công ty
 *     mới lập chưa lên hệ thống thuế là chuyện thường ngày.
 *
 * Và: "không tìm thấy mã" với "chưa tra được" là HAI câu khác nhau. Câu đầu là
 * mã sai, câu sau là mạng hoặc nguồn chết. Gộp làm một là đổ oan cho người nhập.
 *
 * Email nhận hóa đơn vẫn phải gõ tay: cơ quan thuế không công bố email.
 *
 * Quyết định 0129 (bản GNPM) — bản GNPF chép sang 26/09/2026.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "@/hooks/useDebounce";

const VIETQR_BUSINESS_URL = "https://api.vietqr.io/v2/business";

/** Mã số thuế: 10 chữ số, hoặc 10 chữ số + 3 chữ số chi nhánh. Giữ nguyên chuỗi
 *  (KHÔNG parse số) để số 0 đầu không bị nuốt. */
const taxCodePattern = /^[0-9]{10}(-[0-9]{3})?$/;

/**
 * Mã số thuế đã đủ khuôn để đem đi tra chưa.
 *
 * Một nguồn duy nhất cho cả phần kiểm lỗi của ô nhập lẫn phần tra cứu — hai bản
 * sao sẽ trôi khỏi nhau đúng lúc không ai để ý.
 */
export function isValidTaxCode(raw: string): boolean {
  return taxCodePattern.test(raw.trim());
}

export type CompanyInfo = {
  taxCode: string;
  name: string;
  address: string;
  /** Câu trạng thái nguyên văn của nguồn, vd "NNT đang hoạt động". */
  statusText: string;
  /** true CHỈ khi đọc thấy chữ báo ngừng. Trường rỗng hay chữ lạ thì coi như
   *  bình thường — thà không nhắc còn hơn nhắc sai rồi làm người nhập chần chừ. */
  stopped: boolean;
};

export type CompanyLookup =
  | { state: "idle" }
  | { state: "loading" }
  | { state: "found"; company: CompanyInfo }
  /** Nguồn trả lời dứt khoát: mã này không có. */
  | { state: "not-found" }
  /** Chưa hỏi được nguồn: mất mạng, nguồn chết, nguồn trả rác. */
  | { state: "unavailable" };

type VietQrPayload = {
  code?: string;
  data?: {
    id?: string | null;
    name?: string | null;
    address?: string | null;
    status?: string | null;
  } | null;
};

/**
 * Trạng thái là CÂU CHỮ TIẾNG VIỆT, không phải mã: nguồn trả "NNT đang hoạt
 * động". Đừng so bằng `===` — chỉ cảnh báo khi đọc thấy chữ báo ngừng.
 */
const stoppedWords = ["ngừng", "ngưng", "đóng", "chấm dứt", "thu hồi", "tạm nghỉ"];

function readStopped(statusText: string): boolean {
  const lower = statusText.toLowerCase();
  return stoppedWords.some((word) => lower.includes(word));
}

/**
 * Nhớ câu TRẢ LỜI của nguồn, không nhớ lần HỎNG.
 *
 * "Có công ty này" và "không có mã này" đều là câu trả lời — hỏi lại cũng thế.
 * Còn mất mạng thì lần sau có thể khác, nhớ lại là khoá người dùng vào một lỗi
 * đã qua. Sổ này sống theo trang, tải lại trang là sạch.
 */
const answerCache = new Map<string, CompanyLookup>();

/** Chỉ để bài kiểm dựng lại từ đầu giữa các lượt. */
export function resetCompanyLookupCache(): void {
  answerCache.clear();
}

export async function lookupCompanyByTaxCode(rawTaxCode: string): Promise<CompanyLookup> {
  const taxCode = rawTaxCode.trim();
  if (!isValidTaxCode(taxCode)) return { state: "idle" };

  const remembered = answerCache.get(taxCode);
  if (remembered) return remembered;

  let payload: VietQrPayload;
  try {
    const response = await fetch(`${VIETQR_BUSINESS_URL}/${encodeURIComponent(taxCode)}`, {
      headers: { Accept: "application/json" },
    });
    // Nguồn hỏng (5xx, chặn, rate limit) — KHÔNG phải mã sai.
    if (!response.ok) return { state: "unavailable" };
    payload = (await response.json()) as VietQrPayload;
  } catch {
    return { state: "unavailable" };
  }

  // 51 = không có mã, 52 = mã sai khuôn (đo 26/09/2026). Cả hai đều là câu trả
  // lời VỀ CÁI MÃ, nên cùng một câu nhắc và cùng được nhớ.
  if (payload.code === "51" || payload.code === "52") {
    const answer: CompanyLookup = { state: "not-found" };
    answerCache.set(taxCode, answer);
    return answer;
  }

  const data = payload.data;
  // Nói thành công mà không kèm hồ sơ là nguồn trục trặc, không phải mã sai.
  if (payload.code !== "00" || !data) return { state: "unavailable" };

  const statusText = (data.status ?? "").trim();
  const answer: CompanyLookup = {
    state: "found",
    company: {
      taxCode,
      name: (data.name ?? "").trim(),
      address: (data.address ?? "").trim(),
      statusText,
      stopped: readStopped(statusText),
    },
  };
  answerCache.set(taxCode, answer);
  return answer;
}

type AutoFillInput = {
  taxCode: string;
  /** Chỉ tra khi màn đang hỏi thông tin doanh nghiệp (hóa đơn VAT). */
  enabled: boolean;
  name: string;
  address: string;
  /**
   * Điền MỘT LẦN cho cả hai ô.
   *
   * Cố ý KHÔNG tách thành `onName` + `onAddress`: màn nào giữ cả bốn ô trong
   * một object (đúng là cả ba màn hóa đơn của GNPF) thì hai lời gọi liên tiếp
   * cùng đọc bản cũ, và lời sau xoá mất lời trước. Một lời gọi thì không có
   * chỗ nào để lỗi đó xảy ra.
   */
  onFill: (patch: { name?: string; address?: string }) => void;
};

/**
 * Gõ đủ mã, ngừng tay nửa giây → tên và địa chỉ nhảy vào hai ô bên cạnh.
 *
 * Trả về `lookup` (để vẽ dòng nhắc), `showUse` (có gì để mời áp dụng không) và
 * `applyLookup` (người nhập bấm "Dùng thông tin này").
 */
export function useCompanyAutoFill({ taxCode, enabled, name, address, onFill }: AutoFillInput): {
  lookup: CompanyLookup;
  showUse: boolean;
  applyLookup: () => void;
} {
  const debouncedTaxCode = useDebounce(taxCode.trim(), 500);
  const shouldLookup = enabled && isValidTaxCode(debouncedTaxCode);

  /** Câu trả lời kèm mã đã hỏi — có mã thì mới biết nó còn đúng với ô hiện tại. */
  const [answer, setAnswer] = useState<{ taxCode: string; lookup: CompanyLookup } | null>(null);

  /**
   * "Chưa hỏi" và "đang hỏi" SUY RA từ props, không cất vào state. Cất vào
   * state là phải gọi setState ngay trong thân effect mỗi lần mã đổi — đúng
   * kiểu dựng render tầng tầng mà React 19 chặn.
   */
  const lookup: CompanyLookup = useMemo(
    () =>
      !shouldLookup
        ? { state: "idle" }
        : answer?.taxCode === debouncedTaxCode
          ? answer.lookup
          : { state: "loading" },
    [shouldLookup, answer, debouncedTaxCode],
  );

  /**
   * Props mới nhất nằm trong ref, KHÔNG nằm trong mảng phụ thuộc của effect
   * tra cứu. Đưa chúng vào mảng phụ thuộc là dựng sẵn vòng lặp: điền vào ô →
   * props đổi → effect chạy lại → tra và điền tiếp. Ô nhấp nháy.
   */
  const latest = useRef({ name, address, onFill });
  useEffect(() => {
    latest.current = { name, address, onFill };
  });

  /** Mã đã tự điền một lần rồi (luật ②). */
  const filledFor = useRef<string | null>(null);

  useEffect(() => {
    if (!shouldLookup) return;
    let alive = true;
    void lookupCompanyByTaxCode(debouncedTaxCode).then((result) => {
      if (!alive) return;
      setAnswer({ taxCode: debouncedTaxCode, lookup: result });
      if (result.state === "found") autoFill(latest.current, filledFor, result.company);
    });
    return () => {
      alive = false;
    };
  }, [debouncedTaxCode, shouldLookup]);

  const applyLookup = useCallback(() => {
    if (lookup.state !== "found") return;
    const { company } = lookup;
    filledFor.current = company.taxCode;
    // Người nhập tự bấm thì đè cả hai ô — đó chính là điều họ vừa yêu cầu.
    latest.current.onFill({
      ...(company.name ? { name: company.name } : {}),
      ...(company.address ? { address: company.address } : {}),
    });
  }, [lookup]);

  const showUse =
    lookup.state === "found" &&
    ((lookup.company.name !== "" && name.trim() !== lookup.company.name) ||
      (lookup.company.address !== "" && address.trim() !== lookup.company.address));

  return { lookup, showUse, applyLookup };
}

/** Luật ① + ②: chỉ điền ô đang trống, và mỗi mã chỉ điền một lần. */
function autoFill(
  current: { name: string; address: string; onFill: AutoFillInput["onFill"] },
  filledFor: { current: string | null },
  company: CompanyInfo,
): void {
  if (filledFor.current === company.taxCode) return;

  const fillName = company.name !== "" && current.name.trim() === "";
  const fillAddress = company.address !== "" && current.address.trim() === "";
  // Hai ô đều đã có chữ → không đè (luật ①). Chưa điền gì thì cũng chưa ghi sổ
  // "đã điền", để nút mời vẫn còn tác dụng.
  if (!fillName && !fillAddress) return;

  filledFor.current = company.taxCode;
  current.onFill({
    ...(fillName ? { name: company.name } : {}),
    ...(fillAddress ? { address: company.address } : {}),
  });
}
